import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewAddQuestionRule from '@app/src/components/Drawer/Views/DrawerViewAddQuestionRule'
import DrawerViewQuestionRules from '@app/src/components/Drawer/Views/DrawerViewQuestionRules'
import Markdown from '@app/src/components/Markdown'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import { MoveAndDeleteControls } from '@app/src/pages/TemplateBuilder/MoveAndDeleteControls'
import QuestionInputPreview from '@app/src/pages/TemplateBuilder/QuestionInputPreview'
import { SaveRulesValues } from '@app/src/types/questionRules'
import {
  Question,
  QuestionInTemplate,
  QuestionnaireTemplateSection,
  QuestionRule,
} from '@app/src/types/resourceExplorer'
import { QuestionTypes } from '@app/src/wf-constants'
import { AltRouteOutlined, HelpOutlineOutlined, InfoOutlined } from '@mui/icons-material'
import { Alert, Box, Button, Checkbox, FormControlLabel, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/styles'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import RulesDialog from './RulesDialog'
import { FormDataItem } from './TemplateBuilder'

type EditRequestItemProps = {
  item: Partial<FormDataItem>
  questionFromTemplate?: QuestionInTemplate
  sectionIndex: number
  requestItemIndex: number
  removeItem: () => void
  moveUp: () => void
  moveDown: () => void
  questionCount: number
}

const EditQuestionInTemplate: React.FC<EditRequestItemProps> = ({
  item,
  questionFromTemplate,
  requestItemIndex,
  sectionIndex,
  removeItem,
  moveUp,
  moveDown,
  questionCount,
}) => {
  const { register, watch, control } = useFormContext()
  const { formatMessage } = useIntl()
  const { palette } = useTheme()
  const { openDrawer } = useDrawer()
  const { questionRules } = useFlags()
  const [open, openDialog, closeDialog] = useDialogState(false)
  const [disabledRulesButtonInfo, setDisabledRulesButtonInfo] = useState<string>()

  const target = `sections[${sectionIndex}].questions[${requestItemIndex}]`

  const {
    fields: rules,
    append,
    remove: removeRule,
  } = useFieldArray<SaveRulesValues>({
    control,
    name: `${target}.questionnaireQuestionRules`,
  })

  const { data: fetchedQuestion, isFetching } = useFetchResourceWithPost<Question>({
    endpoint: endpoints.questionById(item.questionId),
    key: [FetchKey.Questions, item.questionId],
    body: ['questionType', 'unit', 'requestCollection'],
    options: {
      enabled: Boolean(item.questionId) && !questionFromTemplate,
    },
  })

  const sectionsInTemplate = watch(`sections`)
  const questionInTemplate = watch(target)
  const questionsInTemplate = sectionsInTemplate.flatMap((section: QuestionnaireTemplateSection) => section.questions)
  const rulesInTemplate: QuestionRule[] = sectionsInTemplate
    .flatMap((section: QuestionnaireTemplateSection) =>
      section.questions.flatMap((question: Question) => question.questionnaireQuestionRules),
    )
    ?.filter(Boolean)

  const rulesForCurrentTriggerQuestion = useMemo(
    () => rules?.filter(rule => Number(rule.questionId) === Number(item.questionId)),
    [rules, item.questionId],
  )

  const getQuestionsWithRulesTargetingCurrent = useCallback(
    (allRules: QuestionRule[]) => {
      return allRules
        .filter((rule: QuestionRule) =>
          rule.sectionQuestionTargets.some(
            (target: { targetQuestionId: number }) => target.targetQuestionId === Number(item.questionId),
          ),
        )
        .map((rule: QuestionRule) => Number(rule.questionId))
    },
    [item.questionId],
  )

  const allRules = useMemo(
    () =>
      sectionsInTemplate.flatMap((section: QuestionnaireTemplateSection) =>
        section.questions.flatMap((question: QuestionInTemplate) => question.questionnaireQuestionRules ?? []),
      ),
    [sectionsInTemplate],
  )

  const questionToShow = useMemo(() => questionFromTemplate ?? fetchedQuestion, [questionFromTemplate, fetchedQuestion])

  const isQuestionTypeNumberOrText = useMemo(
    () =>
      questionToShow?.questionType.name === QuestionTypes.Text ||
      questionToShow?.questionType.name === QuestionTypes.Number,
    [questionToShow],
  )

  const questionsWithRulesTargetingCurrent = useMemo(
    () => getQuestionsWithRulesTargetingCurrent(allRules),
    [allRules, getQuestionsWithRulesTargetingCurrent],
  )

  const hasRulesTargetingCurrent = useMemo(
    () =>
      questionsInTemplate.some(
        (question: QuestionInTemplate) =>
          Number(question.questionId) !== Number(item.questionId) &&
          !questionsWithRulesTargetingCurrent.includes(Number(question.questionId)),
      ),
    [questionsInTemplate, item.questionId, questionsWithRulesTargetingCurrent],
  )

  const shouldEnableRulesButton = useMemo(() => {
    if (isQuestionTypeNumberOrText && questionInTemplate?.isRequired) return false

    if (questionsInTemplate.length <= 1) return false

    return hasRulesTargetingCurrent
  }, [isQuestionTypeNumberOrText, questionInTemplate?.isRequired, questionsInTemplate.length, hasRulesTargetingCurrent])

  const rulesForCurrentQuestion = useMemo(
    () =>
      rulesInTemplate?.filter(rule =>
        rule?.sectionQuestionTargets?.some(target => target.targetQuestionId === Number(item.questionId)),
      ),
    [rulesInTemplate, item.questionId],
  )

  const handleSaveRule = useCallback(
    async (values: SaveRulesValues) => {
      append(values)
    },
    [append],
  )

  useEffect(() => {
    if (isQuestionTypeNumberOrText && questionInTemplate?.isRequired) {
      setDisabledRulesButtonInfo(formatMessage({ id: 'templateBuilder.questionRules.uncheckMandatory' }))
      return
    }

    if (questionsInTemplate.length <= 1) {
      setDisabledRulesButtonInfo(formatMessage({ id: 'templateBuilder.questionRules.addMoreQuestions' }))
      return
    }

    if (!hasRulesTargetingCurrent) {
      setDisabledRulesButtonInfo(formatMessage({ id: 'templateBuilder.questionRules.noValidQuestionsToTarget' }))
      return
    }

    setDisabledRulesButtonInfo(undefined)
  }, [isQuestionTypeNumberOrText, questionInTemplate?.isRequired, questionsInTemplate, hasRulesTargetingCurrent])

  useEffect(() => {
    if (!rules.length) return

    const currentQuestionIds = new Set(questionsInTemplate.map((q: QuestionInTemplate) => Number(q.questionId)))

    rules.forEach((rule, index) => {
      const hasValidTargets = rule.sectionQuestionTargets?.some(target =>
        currentQuestionIds.has(Number(target.targetQuestionId)),
      )

      if (!hasValidTargets) {
        removeRule(index)
      }
    })
  }, [questionsInTemplate, rules, removeRule])

  return (
    <Box
      pb={4}
      pt={rulesForCurrentQuestion?.length ? 0 : 4}
      border={`1px solid ${palette.grey['300']}`}
      borderRadius="8px"
      mb={4}
    >
      <input type="hidden" name={`${target}.questionId`} defaultValue={item?.questionId ?? ''} ref={register} />
      <Stack data-testid="question-in-template-builder" spacing={3}>
        {Boolean(rulesForCurrentQuestion?.length) && (
          <Alert variant="filled" severity="info">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">
                {formatMessage({ id: 'templateBuilder.questionRules.skipAlert' })}
              </Typography>
              <Button onClick={openDialog}>{formatMessage({ id: 'templateBuilder.questionRules.seeRules' })}</Button>
            </Stack>
          </Alert>
        )}
        <Stack px={4} direction="row" justifyContent="space-between" alignItems="center">
          {questionToShow?.guidance ? (
            <Tooltip title={<Markdown>{questionToShow.guidance.toString()}</Markdown>}>
              <Box display="flex" alignItems="center">
                <HelpOutlineOutlined fontSize="medium" />
              </Box>
            </Tooltip>
          ) : (
            <Box />
          )}
          <Tooltip title={formatMessage({ id: 'templateBuilder.questionId' })} arrow>
            <Box display="flex" ml="auto" mr={4}>
              <Typography variant="subtitle1">{formatMessage({ id: 'general.id' })}</Typography>
              <Box pl={1}>{questionToShow?.id}</Box>
            </Box>
          </Tooltip>
          <MoveAndDeleteControls
            onDelete={removeItem}
            moveUp={moveUp}
            moveDown={moveDown}
            index={requestItemIndex}
            totalCount={questionCount}
            typeName={formatMessage({ id: 'templateBuilder.fields.controls.type.question' })}
          />
        </Stack>

        <Typography variant="subtitle1" px={4}>
          {sectionIndex + 1}.{requestItemIndex + 1}. {questionToShow?.questionText}
        </Typography>

        {isFetching ? (
          <Box px={4}>
            <Skeleton height={60} />
          </Box>
        ) : (
          <QuestionInputPreview question={questionToShow} />
        )}
        <Stack spacing={2} direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" px={4}>
            <Controller
              name={`${target}.isRequired`}
              control={control}
              defaultValue={item.isRequired ?? false}
              render={({ onChange, value }): JSX.Element => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value}
                      onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => onChange(checked)}
                      color="primary"
                    />
                  }
                  label={formatMessage({ id: 'templateBuilder.mandatoryQuestion' })}
                />
              )}
            />
            <Tooltip title={formatMessage({ id: 'templateBuilder.mandatoryInfo' })}>
              <InfoOutlined fontSize="small" sx={{ color: 'grey.700' }} />
            </Tooltip>
          </Stack>
          {questionToShow && questionRules && (
            <Tooltip title={disabledRulesButtonInfo}>
              <Box px={4}>
                <Button
                  disabled={!shouldEnableRulesButton}
                  startIcon={<AltRouteOutlined />}
                  onClick={() =>
                    openDrawer(
                      rulesForCurrentTriggerQuestion.length ? (
                        <DrawerViewQuestionRules
                          rules={rulesForCurrentTriggerQuestion}
                          removeRule={removeRule}
                          questionToShow={questionToShow}
                          questionInTemplate={questionInTemplate}
                          questionsInTemplate={questionsInTemplate}
                          sectionsInTemplate={sectionsInTemplate}
                          handleSaveRule={handleSaveRule}
                        />
                      ) : (
                        <DrawerViewAddQuestionRule
                          questionToShow={questionToShow}
                          questionsInTemplate={questionsInTemplate}
                          sectionsInTemplate={sectionsInTemplate}
                          handleSaveRule={handleSaveRule}
                          rules={rules}
                          questionInTemplate={questionInTemplate}
                        />
                      ),
                    )
                  }
                >
                  {formatMessage(
                    { id: 'templateBuilder.questionRules.button' },
                    {
                      hasRules: Boolean(rulesForCurrentTriggerQuestion.length),
                      count: rulesForCurrentTriggerQuestion.length,
                    },
                  )}
                </Button>
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <RulesDialog
        open={open}
        closeDialog={closeDialog}
        rulesForCurrentQuestion={rulesForCurrentQuestion}
        questionsInTemplate={questionsInTemplate}
      />
    </Box>
  )
}

export default EditQuestionInTemplate
